<script setup>
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import PageLayout from '../Components/UI/PageLayout.vue'
import MainContainer from '../Components/UI/Content/MainContainer.vue'
import BlockContent from '../Components/UI/Content/BlockContent.vue'
import TableMenu from '../Components/UI/Table/TableMenu.vue'
import CardContent from '../Components/UI/Content/CardContent.vue'
import DataTable from '../Components/UI/Table/DataTable.vue'
import ModalError from '../Components/UI/Modal/ModalError.vue'
import InvoiceDetailModal from '../Components/Invoice/InvoiceDetailModal.vue'
import { getInvoiceListColumns } from '../Constants/InvoiceColumns'
import useApi from '../Composables/useApi'
import useUser from '../../../Composables/User/useUser.ts'
import useSearchParams from '../Composables/useSearchParams'
import useConfig from '../../../Composables/useConfig.ts'
import Role from '../../../Constants/Role.ts'

const { t } = useI18n()
const { getJSON, downloadFile } = useApi()
const { user } = useUser()
const { searchParams } = useSearchParams()
const { isTheme } = useConfig()

const errors = ref([])
const invoiceList = ref(null)
const invoiceListRequest = ref(null)
const invoiceDetailModal = ref(null)

const TYPES = ['CLIENT', 'REFINANCER', 'SUPPLIER']
const TYPE_ROLES = {
  CLIENT: Role.INVOICE_CLIENT_SHOW,
  REFINANCER: Role.INVOICE_REFINANCER_SHOW,
  SUPPLIER: Role.INVOICE_SUPPLIER_SHOW,
}

const roles = computed(() => user.value?.roles || [])

const hasNoFilterRoles = computed(() => !Object.values(TYPE_ROLES).some((role) => roles.value.includes(role)))

const isTypeVisible = (type) => hasNoFilterRoles.value || roles.value.includes(TYPE_ROLES[type])

const submenuLabels = computed(() =>
  TYPES.map((type, id) => ({
    id,
    value: type,
    name: t(`invoice.list.invoice${type.charAt(0) + type.slice(1).toLowerCase()}`),
    visible: isTypeVisible(type),
  })),
)

const initialStatus = () => {
  const fromUrl = parseInt(searchParams.get('s') ?? 0)
  if (TYPES[fromUrl] && isTypeVisible(TYPES[fromUrl])) {
    return fromUrl
  }
  const firstVisible = TYPES.findIndex(isTypeVisible)
  return firstVisible === -1 ? 0 : firstVisible
}

const selectedStatus = ref(initialStatus())
const type = computed(() => TYPES[selectedStatus.value] ?? 'CLIENT')
const filters = ref({
  statuses: [],
  electronicInvoiceStatuses: [],
  companies: [],
  entities: [],
  defaultCompany: null,
})

const filtersValues = ref({})

const filterDefinitions = computed(() => [
  {
    name: 'invoicedAt',
    domain: 'invoice',
    labelTop: true,
    condition: true,
    maxDatetime: filtersValues.value.invoicedAtEnd,
  },
  {
    name: 'invoicedAtEnd',
    domain: 'invoice',
    labelTop: true,
    condition: true,
    minDatetime: filtersValues.value.invoicedAt,
  },
  { name: 'dueAt', domain: 'invoice', labelTop: true, condition: true, maxDatetime: filtersValues.value.dueAtEnd },
  { name: 'dueAtEnd', domain: 'invoice', labelTop: true, condition: true, minDatetime: filtersValues.value.dueAt },
  {
    name: 'company',
    optionsKey: 'companies',
    domain: 'invoice',
    labelTop: true,
    condition: filters.value.companies.length > 1,
    label: 'invoice.fields.company.label',
  },
  {
    name: 'entity',
    optionsKey: 'entities',
    domain: 'invoice',
    labelTop: true,
    condition: true,
    label: type.value === 'SUPPLIER' ? 'invoice.fields.supplier.label' : 'invoice.fields.client.label',
  },
  { name: 'number', domain: 'invoice', labelTop: true, condition: true },
  { name: 'status', optionsKey: 'statuses', domain: 'invoice', labelTop: true, typeValue: 'string', condition: true },
  {
    name: 'electronicInvoiceStatus',
    optionsKey: 'electronicInvoiceStatuses',
    domain: 'invoice',
    labelTop: true,
    typeValue: 'string',
    condition: filters.value.electronicInvoiceStatuses.length > 1,
  },
  { name: 'amount', domain: 'invoice', labelTop: true, condition: true },
  { name: 'amountVat', domain: 'invoice', labelTop: true, condition: true },
  { name: 'balance', domain: 'invoice', labelTop: true, condition: true },
])

const tableFilters = computed(() => filterDefinitions.value.filter(({ condition }) => condition === true))

const filterNames = computed(() => filterDefinitions.value.map(({ name }) => name))

const emptyFiltersValues = () => Object.fromEntries(filterNames.value.map((name) => [name, null]))

const resetFilters = () => {
  filterNames.value.forEach((name) => searchParams.delete(name))
  filtersValues.value = emptyFiltersValues()
}

const columns = computed(() => getInvoiceListColumns(t, type.value, isTheme))

const loadFilterOptions = async () => {
  try {
    const options = await getJSON(`invoice/filter-options?${searchParams.toString()}`)
    const previous = filters.value
    const merged = { ...previous, ...options }

    for (const { name, optionsKey } of filterDefinitions.value) {
      const selected = filtersValues.value[name]
      if (!optionsKey || selected === null || selected === undefined || selected === '') {
        continue
      }

      const isPresent = (merged[optionsKey] ?? []).some((option) => String(option.id) === String(selected))
      if (!isPresent) {
        const previousOption = (previous[optionsKey] ?? []).find((option) => String(option.id) === String(selected))
        if (previousOption) {
          merged[optionsKey] = [previousOption, ...(merged[optionsKey] ?? [])]
        }
      }
    }

    filters.value = merged

    if (!filtersValues.value.company && options.defaultCompany) {
      filtersValues.value.company = options.defaultCompany
      searchParams.set('company', options.defaultCompany)
    }
  } catch {
    errors.value.push(t('error.loadingFilterOptions'))
  }
}

const updateFilterData = async (data) => {
  filtersValues.value = { ...filtersValues.value, ...data }
  cancelPendingRequest()

  for (const [name, value] of Object.entries(data)) {
    if (value !== null && value !== undefined && value !== '') {
      searchParams.set(name, value)
    } else {
      searchParams.delete(name)
    }
  }

  invoiceList.value?.renderDataTable()
  await loadFilterOptions()
}

const setSelectedStatus = (value) => {
  value = parseInt(value)
  if (value !== selectedStatus.value) {
    selectedStatus.value = value
    resetFilters()
    updateParams()
  }
}

const updateParams = async () => {
  cancelPendingRequest()
  searchParams.set('s', selectedStatus.value)
  searchParams.set('type', type.value)
  invoiceList.value?.renderDataTable()
  await loadFilterOptions()
}

const cancelPendingRequest = () => {
  invoiceListRequest.value?.cancel()
}

const getInvoices = async (callback, { page, limit, search, sort = [] }) => {
  cancelPendingRequest()

  const newUrlSearchParams = new URLSearchParams(searchParams.toString())

  newUrlSearchParams.set('page', page.toString())
  newUrlSearchParams.set('limit', limit.toString())

  for (const { field, order } of sort) {
    newUrlSearchParams.set(`sort[${field}]`, order)
  }

  if (search) {
    newUrlSearchParams.set('q', search)
  }

  searchParams.value = newUrlSearchParams

  const getJsonPromise = getJSON(`invoices?${newUrlSearchParams.toString()}`)
  invoiceListRequest.value = getJsonPromise
  const { items: invoices, total } = await getJsonPromise
  callback({ data: invoices, recordsTotal: total, recordsFiltered: total })
}

const isExporting = ref(false)

const download = async () => {
  isExporting.value = true
  try {
    const params = new URLSearchParams(searchParams.toString())
    params.delete('page')
    params.delete('limit')

    const pad = (n) => String(n).padStart(2, '0')
    const d = new Date()
    const formatDate = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}-${pad(d.getHours())}-${pad(d.getMinutes())}-${pad(d.getSeconds())}`

    await downloadFile(`invoices/export?${params}`, `Factures-${formatDate}.xlsx`)
  } finally {
    isExporting.value = false
  }
}

const openInvoiceModal = (id) => invoiceDetailModal.value?.openModal(id)
const closeInvoiceModal = () => invoiceDetailModal.value?.closeModal()

const onTableClick = async (e, table) => {
  const icon = e.target.closest('tr i')
  if (icon && table.contains(icon)) {
    const id = icon.id
    const date = new Date()
    const formatDate = `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`
    await downloadFile(`invoices/download?id=${id}`, `Facture-${id}-${formatDate}.pdf`)
    return
  }

  const link = e.target.closest('a.invoice-detail')
  if (link && table.contains(link)) {
    openInvoiceModal(Number(link.dataset.id))
  }
}

onMounted(async () => {
  const table = document.getElementById('listeFacture')
  table?.addEventListener('click', (e) => onTableClick(e, table))

  await updateParams()
})
</script>
<template>
  <PageLayout menu="invoices-index">
    <MainContainer title-t="menu.myInvoice">
      <BlockContent>
        <TableMenu
          :key="type"
          :status="selectedStatus"
          :filters="tableFilters"
          :filter-options="filters"
          :filter-values="filtersValues"
          :submenu-labels="submenuLabels"
          :show-download-button="true"
          :show-calendar-filter="false"
          endpoint="invoice"
          @set-selected-status="setSelectedStatus"
          @download="download"
          @update-filter-data="updateFilterData"
        />
        <CardContent>
          <DataTable
            id="listeFacture"
            ref="invoiceList"
            :columns="columns"
            :fetch="getInvoices"
            :order="[{ dir: 'desc', name: 'createdAt' }]"
            :zero-records="t('invoice.list.zeroRecords')"
            :first-load="false"
            pagination-mode="server"
          />
        </CardContent>
      </BlockContent>
    </MainContainer>
    <InvoiceDetailModal id="invoice-list-detail" ref="invoiceDetailModal" @icon-close="closeInvoiceModal" />
    <ModalError v-if="errors.length > 0" id="modalError" ref="modalError" :errors="errors" />
  </PageLayout>
</template>
